import React, { useMemo, useState } from 'react';
import { LineChart, BarList, Donut } from '../Security/Charts';
import './modules.css';

function Bars({ points, height = 150 }) {
  const W = 600, H = height, P = { l: 34, r: 8, t: 8, b: 22 };
  if (!points || points.length === 0) return <div className="muted">No data</div>;
  const max = Math.max(1, ...points.map((p) => p.value));
  const bw = (W - P.l - P.r) / points.length;
  const step = Math.max(1, Math.ceil(points.length / 8));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="sec-chart" role="img">
      <line x1={P.l} x2={W - P.r} y1={H - P.b} y2={H - P.b} stroke="#e2e8f0" />
      <text x={P.l - 4} y={P.t + 8} textAnchor="end" fontSize="10" fill="#718096">{Math.round(max)}</text>
      {points.map((p, i) => {
        const h = (p.value / max) * (H - P.t - P.b);
        return (
          <g key={i}>
            <rect x={P.l + i * bw + 1} y={H - P.b - h} width={Math.max(1, bw - 2)} height={h} fill="#2d66e2" rx="2"><title>{p.label}: {p.value}</title></rect>
            {i % step === 0 && <text x={P.l + i * bw + bw / 2} y={H - 6} textAnchor="middle" fontSize="10" fill="#718096">{p.label}</text>}
          </g>
        );
      })}
    </svg>
  );
}

function DataTable({ table }) {
  const [sort, setSort] = useState({ col: -1, dir: 1 });
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(25);

  const rows = useMemo(() => {
    let r = table.rows;
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      r = r.filter((row) => row.some((c) => String(c).toLowerCase().includes(t)));
    }
    if (sort.col >= 0) {
      const num = (v) => parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
      r = [...r].sort((a, b) => {
        const x = a[sort.col], y = b[sort.col];
        const nx = num(x), ny = num(y);
        const cmp = !Number.isNaN(nx) && !Number.isNaN(ny) && /\d/.test(x) && /\d/.test(y) ? nx - ny : String(x).localeCompare(String(y));
        return cmp * sort.dir;
      });
    }
    return r;
  }, [table.rows, q, sort]);

  return (
    <div className="sec-card sec-scroll" style={{ marginBottom: 16 }}>
      <div className="sec-row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>{table.title}</h3>
        {table.rows.length > 8 && (
          <input className="sec-input" style={{ minWidth: 160, width: 200 }} placeholder="Filter rows…" value={q} onChange={(e) => setQ(e.target.value)} />
        )}
      </div>
      {table.rows.length === 0 ? <div className="muted">Nothing to show for this period.</div> : (
        <>
          <table className="sec-table">
            <thead>
              <tr>{table.columns.map((c, i) => (
                <th key={i} style={{ cursor: 'pointer' }} onClick={() => setSort((s) => ({ col: i, dir: s.col === i ? -s.dir : 1 }))}>
                  {c}{sort.col === i ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.slice(0, limit).map((row, ri) => (
                <tr key={ri}>{row.map((c, ci) => <td key={ci} className={ci === 0 ? 'sec-mono' : ''}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
          {rows.length > limit && (
            <div className="sec-pager">
              <span className="muted">Showing {limit} of {rows.length}</span>
              <button className="ghost" onClick={() => setLimit(limit + 50)}>Show more</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Heat({ h }) {
  const max = Math.max(1, ...h.matrix.flat());
  return (
    <div className="sec-card sec-scroll" style={{ marginBottom: 16 }}>
      <h3>{h.title}</h3>
      <table className="mod-heat">
        <thead><tr><th />{h.colLabels.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
        <tbody>
          {h.matrix.map((row, r) => (
            <tr key={r}>
              <th>{h.rowLabels[r]}</th>
              {row.map((v, c) => (
                <td key={c} title={String(v)} style={{ background: `rgba(45,102,226,${v ? 0.12 + 0.88 * (v / max) : 0.05})`, color: v / max > 0.55 ? '#fff' : 'inherit' }}>{v ? Math.round(v * 10) / 10 : ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Renders the generic InsightResult returned by GET /insights/{key}. */
export default function InsightView({ result, compact = false }) {
  if (!result) return null;
  const { kpis = [], series = [], breakdowns = [], heatmaps = [], tables = [], notes = [] } = result;
  return (
    <div>
      {kpis.length > 0 && (
        <div className="sec-kpis">
          {kpis.map((k, i) => (
            <div key={i} className={`sec-kpi mod-kpi-${k.tone || ''}`}>
              <small>{k.label}</small>
              <b>{k.value}</b>
              {k.hint && <small>{k.hint}</small>}
            </div>
          ))}
        </div>
      )}

      {(series.length > 0 || breakdowns.length > 0) && (
        <div className="sec-grid">
          {series.map((s, i) => (
            <div className="sec-card" key={`s${i}`}>
              <h3>{s.title}</h3>
              {s.kind === 'bar'
                ? <Bars points={s.points} />
                : <LineChart data={s.points.map((p) => ({ label: p.label, v: p.value }))} series={[{ key: 'v', label: s.name }]} />}
            </div>
          ))}
          {breakdowns.map((b, i) => (
            <div className="sec-card" key={`b${i}`}>
              <h3>{b.title}</h3>
              {b.items.length === 0 ? <div className="muted">No data</div>
                : b.kind === 'donut'
                  ? <Donut items={b.items.map((p) => ({ label: p.label, value: p.value }))} />
                  : <BarList items={b.items.map((p) => ({ label: p.label, value: p.value }))} />}
            </div>
          ))}
        </div>
      )}

      {heatmaps.map((h, i) => <Heat key={i} h={h} />)}
      {tables.map((t, i) => <DataTable key={i} table={t} />)}
      {!compact && notes.map((n, i) => <p className="mod-note" key={i}>ⓘ {n}</p>)}
    </div>
  );
}
